import { useState, useEffect } from 'react';
import Checkbox from 'components/checkbox';
import {
  MdSearch,
  MdCheck,
  MdExpandMore,
  MdExpandLess,
  MdClose,
} from 'react-icons/md';

const TopicSelectionModal = ({
  isOpen,
  onClose,
  lectureData = [],
  selectedSubtopics = [],
  setSelectedSubtopics,
  sentTopics = [],
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedDays, setExpandedDays] = useState({});

  useEffect(() => {
    if (isOpen && lectureData.length > 0) {
      const initialExpanded = {};
      lectureData.forEach((lecture) => {
        initialExpanded[lecture.lectureDay] = true;
      });
      setExpandedDays(initialExpanded);
    }
  }, [isOpen, lectureData]);

  if (!isOpen) return null;

  const isTopicSent = (topicTitle, lectureDay) => {
    const daySent = sentTopics.find((s) => s.lectureDay === lectureDay);
    if (daySent && daySent.subtopics) {
      return daySent.subtopics.includes(topicTitle);
    }
    return false;
  };

  const getTotalSentTopics = () => {
    return sentTopics.reduce((total, day) => {
      return total + (day.subtopics ? day.subtopics.length : 0);
    }, 0);
  };

  const getSelectedInDay = (dayTopics, lectureDay) => {
    return dayTopics.filter((t) => selectedSubtopics.includes(t)).length;
  };

  const getSentInDay = (dayTopics, lectureDay) => {
    return dayTopics.filter((t) => isTopicSent(t, lectureDay)).length;
  };

  const toggleDay = (day) => {
    setExpandedDays((prev) => ({
      ...prev,
      [day]: !prev[day],
    }));
  };

  const handleTopicToggle = (topicTitle) => {
    setSelectedSubtopics((prev) =>
      prev.includes(topicTitle)
        ? prev.filter((t) => t !== topicTitle)
        : [...prev, topicTitle]
    );
  };

  const selectAllInDay = (dayTopics) => {
    const newTopics = dayTopics.filter((t) => !selectedSubtopics.includes(t));
    setSelectedSubtopics((prev) => [...prev, ...newTopics]);
  };

  const clearAllInDay = (dayTopics) => {
    setSelectedSubtopics((prev) => prev.filter((t) => !dayTopics.includes(t)));
  };

  const selectAll = () => {
    const allTopics = lectureData.flatMap((l) =>
      l.subtopics.map((s) => s.title)
    );
    setSelectedSubtopics(allTopics);
  };

  const clearAll = () => {
    setSelectedSubtopics([]);
  };

  const getFilteredData = () => {
    if (!searchTerm.trim()) return lectureData;
    return lectureData
      .map((lecture) => ({
        ...lecture,
        subtopics: lecture.subtopics.filter((sub) =>
          sub.title.toLowerCase().includes(searchTerm.toLowerCase())
        ),
      }))
      .filter((lecture) => lecture.subtopics.length > 0);
  };

  const filteredData = getFilteredData();

  return (
    <div className="bg-black fixed inset-0 z-50 flex items-center justify-center bg-opacity-50">
      <div className="mx-2 max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-xl bg-white shadow-xl dark:bg-navy-800">
        <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-white/10">
          <div>
            <h3 className="text-xl font-bold text-navy-700 dark:text-white">
              Select Topics
            </h3>
            <p className="text-sm text-gray-500">
              {selectedSubtopics.length} selected
              {sentTopics.length > 0 && (
                <span className="ml-2">
                  ({getTotalSentTopics()} already sent)
                </span>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-1 text-gray-500 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-navy-700"
          >
            <MdClose className="h-6 w-6" />
          </button>
        </div>

        <div className="border-b border-gray-200 p-4 dark:border-white/10">
          <div className="relative">
            <input
              type="text"
              placeholder="Search topics..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-4 py-2 pl-10 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
            />
            <MdSearch className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          </div>
        </div>

        <div className="max-h-[50vh] space-y-2 overflow-y-auto p-4">
          {filteredData.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              {searchTerm
                ? 'No topics found matching your search'
                : 'No topics available'}
            </div>
          ) : (
            filteredData.map((lecture) => {
              const dayTopics = lecture.subtopics.map((s) => s.title);
              const selectedInDay = dayTopics.filter((t) =>
                selectedSubtopics.includes(t)
              ).length;
              const allInDaySelected =
                dayTopics.length > 0 && selectedInDay === dayTopics.length;

              return (
                <div
                  key={lecture.lectureDay}
                  className="rounded-lg border border-gray-200 dark:border-white/10"
                >
                  <button
                    onClick={() => toggleDay(lecture.lectureDay)}
                    className="flex w-full items-center justify-between rounded-lg bg-gray-50 p-3 text-left hover:bg-gray-100 dark:bg-navy-700 dark:hover:bg-navy-600"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-semibold text-navy-700 dark:text-white">
                        Day {lecture.lectureDay}
                      </span>
                      <span className="text-sm text-gray-500">
                        ({selectedInDay}/{dayTopics.length} new
                        {getSentInDay(dayTopics, lecture.lectureDay) > 0 &&
                          ` • ${getSentInDay(
                            dayTopics,
                            lecture.lectureDay
                          )} sent`}
                        )
                      </span>
                    </div>
                    {expandedDays[lecture.lectureDay] ? (
                      <MdExpandLess className="h-5 w-5 text-gray-500" />
                    ) : (
                      <MdExpandMore className="h-5 w-5 text-gray-500" />
                    )}
                  </button>

                  {expandedDays[lecture.lectureDay] && (
                    <div className="border-t border-gray-200 p-3 dark:border-white/10">
                      <p className="mb-2 text-sm text-gray-600 dark:text-gray-300">
                        {lecture.topic}
                      </p>

                      <div className="mb-3 flex gap-2">
                        <button
                          onClick={() => selectAllInDay(dayTopics)}
                          disabled={allInDaySelected}
                          className="rounded px-2 py-1 text-xs font-medium text-brand-600 hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-navy-700"
                        >
                          Select All
                        </button>
                        <button
                          onClick={() => clearAllInDay(dayTopics)}
                          disabled={selectedInDay === 0}
                          className="rounded px-2 py-1 text-xs font-medium text-red-500 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-navy-700"
                        >
                          Clear All
                        </button>
                      </div>

                      <div className="space-y-2">
                        {lecture.subtopics.map((sub, i) => {
                          const isSelected = selectedSubtopics.includes(
                            sub.title
                          );
                          const wasSent = isTopicSent(
                            sub.title,
                            lecture.lectureDay
                          );
                          return (
                            <div
                              key={i}
                              className={`flex items-center gap-2 rounded p-2 hover:bg-gray-50 dark:hover:bg-navy-700 ${
                                wasSent
                                  ? 'bg-gray-100 dark:bg-gray-800'
                                  : isSelected
                                  ? 'bg-green-50 dark:bg-green-900/30'
                                  : ''
                              }`}
                            >
                              <Checkbox
                                checked={isSelected || wasSent}
                                onChange={() => {
                                  if (!wasSent) {
                                    handleTopicToggle(sub.title);
                                  }
                                }}
                              />
                              <span
                                className={
                                  wasSent
                                    ? 'text-gray-500 dark:text-gray-400'
                                    : isSelected
                                    ? 'font-medium text-green-700 dark:text-green-400'
                                    : 'text-gray-600 dark:text-gray-300'
                                }
                              >
                                {sub.title}
                              </span>
                              {wasSent && (
                                <span className="ml-1 rounded bg-gray-200 px-1.5 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                                  Sent
                                </span>
                              )}
                              {!wasSent && isSelected && (
                                <MdCheck className="ml-auto h-4 w-4 text-green-500" />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="flex items-center justify-between border-t border-gray-200 p-4 dark:border-white/10">
          <div className="flex gap-2">
            <button
              onClick={selectAll}
              className="rounded-lg px-3 py-2 text-sm font-medium text-brand-600 hover:bg-brand-50 dark:hover:bg-navy-700"
            >
              Select All
            </button>
            <button
              onClick={clearAll}
              disabled={selectedSubtopics.length === 0}
              className="rounded-lg px-3 py-2 text-sm font-medium text-red-500 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-navy-700"
            >
              Clear All
            </button>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg bg-brand-900 px-6 py-2 font-medium text-white hover:bg-brand-800 dark:bg-brand-400 dark:hover:bg-brand-300"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default TopicSelectionModal;
